
import { fetchCardLinkedPolls } from '@/utils/supabaseHelpers';
import { useState, useEffect, useCallback } from 'react';
import { Poll } from '@/types/poll';
import { useCollectionRealtime } from '../realtime/useCollectionRealtime';

interface UseLinkedPollsResult {
  linkedPolls: Poll[];
  isLoading: boolean;
  handleRefreshPolls: () => void;
  isConnected: boolean;
}

export const useLinkedPolls = (cardId: string, linkedPollId?: string | null): UseLinkedPollsResult => {
  const [linkedPolls, setLinkedPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLinkedPolls = useCallback(async () => {
    if (!cardId) return;
    
    setIsLoading(true);
    try {
      const polls = await fetchCardLinkedPolls(cardId, linkedPollId);
      console.log('useLinkedPolls: Fetched polls for card', cardId, ':', polls.length);
      setLinkedPolls(polls);
    } catch (error) {
      console.error('Error fetching linked polls:', error);
      setLinkedPolls([]);
    } finally {
      setIsLoading(false);
    }
  }, [cardId, linkedPollId]);

  // Handle poll updates from universal real-time system
  const handlePollUpdate = useCallback((updatedPoll: Poll) => {
    console.log('useLinkedPolls: Received poll update:', updatedPoll);
    setLinkedPolls(currentPolls => 
      currentPolls.map(poll => 
        poll.id === updatedPoll.id ? updatedPoll : poll
      )
    );
  }, []);

  // Set up universal real-time subscription for poll updates
  const { isConnected } = useCollectionRealtime(
    'polls',
    {}, // Listen to all poll changes
    {
      onItemUpdated: useCallback((payload: any) => {
        const updatedPoll = payload;
        
        // Check if this update affects any of our linked polls
        const affectedPoll = linkedPolls.find(poll => poll.id === updatedPoll.id);
        if (affectedPoll) {
          console.log('useLinkedPolls: Poll update affects linked poll:', updatedPoll.id);
          handlePollUpdate(updatedPoll);
        }
      }, [linkedPolls, handlePollUpdate]),
      
      onError: useCallback((error: any) => {
        console.error('Linked polls real-time error:', error);
      }, [])
    },
    linkedPolls.length > 0 // Only enabled if we have linked polls
  );

  useEffect(() => {
    fetchLinkedPolls();
  }, [fetchLinkedPolls]);

  const handleRefreshPolls = useCallback(() => {
    console.log('useLinkedPolls: Manual refresh requested for card', cardId);
    fetchLinkedPolls();
  }, [fetchLinkedPolls]);

  return {
    linkedPolls,
    isLoading,
    handleRefreshPolls,
    isConnected,
  };
};
